import {
  Box,
  Center,
  Container,
  Flex,
  Spacer,
  Spinner,
  useDisclosure,
  useUpdateEffect,
} from '@chakra-ui/react';
import useAccountIdQueryParam from 'hooks/modules/brokerAccount/useAccountIdQueryParam';
import usePeriodQueryParam from 'hooks/modules/period/usePeriodQueryParam';
import EmptyProfileCard from 'presentation/components/modules/home/EmptyProfileCard';
import GeneralSurveyCard from 'presentation/components/modules/survey/GeneralSurveyCard';
import ServicesGrid from 'presentation/components/modules/home/ServicesGrid';
import StrategiesShortGrid from 'presentation/components/modules/strategies/grid/StrategiesShortGrid';
import ProductsShortGrid from 'presentation/components/modules/products/grid/ProductsShortGrid';
import NoAccountsCard from 'presentation/components/modules/portfolio/NoAccountsCard';
import AppCard from 'presentation/components/shared/card/AppCard';
import ErrorContainer from 'presentation/components/shared/ErrorContainer';
import HomeAccountsContainer from 'presentation/containers/modules/home/HomeAccountsContainer';
import HomeChartContainer from 'presentation/containers/modules/home/HomeChartContainer';
import HomePeCompanyOffersContainer from 'presentation/containers/modules/home/HomePeCompanyOffersContainer';
import HomeSelfIpoContainer from 'presentation/containers/modules/home/HomeSelfIpoContainer';
import HomeRecommendationsContainer from 'presentation/containers/modules/home/HomeRecommendationsContainer';
import GeneralCompensationCard from 'presentation/components/modules/compensation/GeneralCompensationCard';
import RequestServiceManagerCallModal from 'presentation/containers/modules/services/RequestServiceManagerCallModal';
import PageLayout from 'presentation/layout/shared/page/PageLayout';
import SidebarLayout, {
  sidebarWidth,
} from 'presentation/layout/shared/SidebarLayout';
import { FC, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useFetchHomeQuery } from 'rtkQuery/sharespro/queries/home';
import { useFetchServiceProductListQuery } from 'rtkQuery/sharespro/queries/services';
import PersonalManagerChatCard from 'presentation/containers/modules/chat/detail/PersonalManagerChatCard';
import { ChatRoomType } from 'enums/modules/chat/chatRoomType';
import OnlineDocumentsHomeCard from 'presentation/components/modules/onlineDocuments/OnlineDocumentsHomeCard';
import HomeRebalancingContainer from 'presentation/containers/modules/home/HomeRebalancingContainer';

interface ServiceModalData {
  serviceId: ID;
  title: string;
  text: string;
}

const HomePage: FC = () => {
  const [accountId] = useAccountIdQueryParam();
  const [period] = usePeriodQueryParam();
  const { data, error, refetch, isFetching } = useFetchHomeQuery({
    period,
    account_id: accountId,
  });
  const { data: products } = useFetchServiceProductListQuery();

  const [serviceModalData, setServiceModalData] =
    useState<ServiceModalData | null>(null);

  const {
    isOpen: isServiceManagerCallOpen,
    onClose: onServiceManagerCallClose,
    onOpen: onServiceManagerCallOpen,
  } = useDisclosure();

  const { t, i18n } = useTranslation();

  useUpdateEffect(() => {
    refetch();
  }, [i18n.language]);

  let content;
  let hasData = false;
  if (data) {
    const sections = (
      <>
        {data.isShowGeneralSurvey && <GeneralSurveyCard mb="20px" />}
        {data.unsignedDocuments.length > 0 && (
          <OnlineDocumentsHomeCard mb="20px" />
        )}

        {data.total_compensation_amount > 0 &&
          data.total_compensation_progress > 0 && (
            <GeneralCompensationCard
              amount={data.total_compensation_amount}
              progress={data.total_compensation_progress}
            />
          )}
        <Spacer h="20px" />
        <HomeRebalancingContainer/>
        <HomeRecommendationsContainer />
        <HomeSelfIpoContainer />
        <HomePeCompanyOffersContainer />
        {products && products.length > 0 && (
          <>
            <ProductsShortGrid products={products} />
            <Spacer h="20px" />
          </>
        )}
        <StrategiesShortGrid strategies={data.strategies} />
        <Spacer h="20px" />
        <ServicesGrid
          onGridItemClick={(id, title) => {
            setServiceModalData({
              serviceId: id,
              title,
              text: t('requestCallManagerServiceText'),
            });
            onServiceManagerCallOpen();
          }}
          serviceList={data.services}
        />
        {serviceModalData && (
          <RequestServiceManagerCallModal
            text={serviceModalData.text}
            title={serviceModalData.title}
            isOpen={isServiceManagerCallOpen}
            onClose={onServiceManagerCallClose}
            serviceId={serviceModalData.serviceId}
          />
        )}
      </>
    );
    if (data.accounts.items.length === 0) {
      content = (
        <Container>
          <Flex mb="20px">
            {!data.isProfileFilled && <EmptyProfileCard flex="1" mr="15px" />}
            <NoAccountsCard flex="1" />
          </Flex>
          {sections}
        </Container>
      );
    } else {
      content = (
        <Container display="flex" alignItems="flex-start">
          <SidebarLayout>
            <HomeAccountsContainer />
            <PersonalManagerChatCard
              mt="20px"
              roomType={ChatRoomType.PersonalManager}
            />
          </SidebarLayout>
          <Box flex="1" w={`calc(100% - ${sidebarWidth}px - 20px)`}>
            <HomeChartContainer />
            <Spacer h="20px" />
            {!data.isProfileFilled && <EmptyProfileCard mb="20px" />}
            {sections}
          </Box>
        </Container>
      );
    }
    hasData = true;
  } else if (isFetching) {
    content = <Spinner />;
  } else if (error) {
    content = (
      <ErrorContainer
        error={error}
        onRetry={() => {
          refetch();
        }}
      />
    );
  } else {
    content = <Spinner />;
  }

  if (!hasData) {
    content = (
      <Container>
        <AppCard p="40px">
          <Center>{content}</Center>
        </AppCard>
      </Container>
    );
  }

  return (
    <PageLayout>
      <Helmet>
        <title>{t('navMain')}</title>
      </Helmet>
      {content}
    </PageLayout>
  );
};

export default HomePage;
